import { useEffect, useState } from "react";

/**
 * Cycles a fixed-size window over a longer list, so a shared display can show
 * everyone in turn instead of permanently showing the first few.
 *
 * Returns the index the visible slice starts at. The window advances by a full
 * page each tick and wraps, so every entry gets its turn and nobody is shown
 * twice before everyone has been shown once.
 *
 * Lists that already fit hold still: no timer is started and the window stays
 * at zero, which also keeps small family games completely static.
 */
export function useRotatingWindow(
  total: number,
  size: number,
  intervalMs: number,
): number {
  const [start, setStart] = useState(0);
  const rotates = size > 0 && total > size && intervalMs > 0;

  useEffect(() => {
    if (!rotates) {
      setStart(0);
      return;
    }
    const interval = window.setInterval(() => {
      setStart((prev) => {
        const next = prev + size;
        return next >= total ? 0 : next;
      });
    }, intervalMs);
    return () => window.clearInterval(interval);
  }, [rotates, total, size, intervalMs]);

  // A list that shrank between renders must not leave the window past its end.
  return start >= total ? 0 : start;
}
